import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import DemoContent from './DemoContent';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

const PowerPointGenerator = () => {
  const { updateCreditsFromResponse } = useCredits();
  const [content, setContent] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState(null);
  const [error, setError] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  const handleFileChange = (e) => {
    setTemplateFile(e.target.files[0]);
  };

  const handleLogoChange = (e) => {
    setLogoFile(e.target.files[0]);
  };

  const handleGenerate = async () => {
    if (!content.trim()) {
      setError('Please enter some content to generate slides.');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedSlides(null);
    setDownloadUrl('');

    try {
      let response;
      if (templateFile || logoFile) {
        const formData = new FormData();
        formData.append('content', content);
        if (templateFile) formData.append('template', templateFile);
        if (logoFile) formData.append('logo', logoFile);
        response = await axios.post(getApiUrl('/generate'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
      } else {
        response = await axios.post(getApiUrl('/generate'), { content }, {
          withCredentials: true,
        });
      }
      setGeneratedSlides(response.data.slides);
      setDownloadUrl(response.data.downloadUrl);
      // Update credits from response
      updateCreditsFromResponse(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate slides. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    
    console.log('Downloading from:', downloadUrl);
    
    try {
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        },
        withCredentials: true,
      });
      
      console.log('Download response:', response);
      
      // Create blob URL
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated_deck.pptx');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download the presentation. Please try again.');
    }
  };

  const handleLoadDemo = (demoText) => {
    setContent(demoText);
    setError('');
    setGeneratedSlides(null);
    setDownloadUrl('');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            PowerPoint Generator
          </h1>
          <p className="text-xl text-gray-600">
            Transform any content into professional PowerPoint presentations
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content to Convert
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste your content here (blog post, article, notes, etc.)"
                className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Demo Content Buttons */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Try with demo content:</h3>
              <DemoContent onLoadDemo={handleLoadDemo} />
            </div>

            {/* Template Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Optional: Upload PowerPoint Template (.pptx)</label>
              <input
                type="file"
                accept=".pptx"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {templateFile && <span className="text-xs text-gray-600 mt-1 block">Selected: {templateFile.name}</span>}
            </div>
            {/* Logo Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Optional: Upload Company Logo (PNG, JPG)</label>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleLogoChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {logoFile && <span className="text-xs text-gray-600 mt-1 block">Selected: {logoFile.name}</span>}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {content.length} characters
              </span>
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !content.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Slides
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Output Section */}
          <div>
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-800">{error}</span>
                </div>
              </div>
            )}
            {/* Generated Slides Preview */}
            {generatedSlides && (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900">Generated Slides</h2>
                  </div>
                  {downloadUrl && (
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PPTX
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {generatedSlides.map((slide, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">
                        Slide {index + 1}: {slide.title}
                      </h3>
                      <ul className="space-y-1">
                        {slide.bullets.map((bullet, bulletIndex) => (
                          <li key={bulletIndex} className="flex items-start">
                            <span className="text-green-600 mr-2 mt-1">✓</span>
                            <span className="text-gray-700">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">How it works</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</div>
                  <div>Paste your content (blog post, article, or any text) into the input field above.</div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</div>
                  <div>Click "Generate Slides" to create a professional PowerPoint presentation using AI.</div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</div>
                  <div>Preview the generated slides and download the PowerPoint file when ready.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerPointGenerator; 